import { useNeedPay } from 'modules/admin';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
    LeadCloudFileType,
    RecurrentData,
    TaskAnswerOptionType,
    TaskTemplateCardType,
    TaskTemplateSaveDataType,
    UserType,
} from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { getCloudflareUploadUrls, linkCloudflareFiles } from 'api/apiCloudflare';
import { cloneTaskTemplate, createTaskTemplate, getTaskTemplate, updateTaskTemplate } from 'api/apiTask';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { isRecurrentDataValid } from '../../shared/recurrent/emptyRecurrent';
import { templateUserToForm } from './templateUser';

interface UseOffTaskDirParams {
    show: boolean;
    templateId: number | null;
    onHide: () => void;
    onSaved: (template?: TaskTemplateCardType) => void;
    /** Крок процесу: повторення не показується і не зберігається. */
    disableRecurrence?: boolean;
    /** Зберегти як новий шаблон і скопіювати файли з цього id. Оригінал не змінюється. */
    forkFromTemplateId?: number | null;
    /** Рядок пишеться в довідник задач, але не з’являється в його списку. */
    hideFromDirectory?: boolean;
}

export interface DirFormState {
    taskTypeId: number | null;
    categoryId: number | null;
    taskName: string;
    taskInfo: string;
    executor: UserType | null;
    controller: UserType | null;
    reserveExecutorTime: boolean;
    executorCanChangeTerm: boolean;
    noEarlyStart: boolean;
    pay: boolean;
    payAmount: number | null;
    payUsd: boolean;
    showPayToExecutor: boolean;
    recurrentEnabled: boolean;
    recurrentData: RecurrentData | null;
    recurrentAllowOverlap: boolean;
    answers: TaskAnswerOptionType[];
    /** Лише форма. У базі немає: увімкнено, якщо задано termDays. */
    termEnabled: boolean;
    termDays: number | null;
}

const emptyForm = (): DirFormState => ({
    taskTypeId: null,
    categoryId: null,
    taskName: '',
    taskInfo: '',
    executor: null,
    controller: null,
    reserveExecutorTime: false,
    executorCanChangeTerm: false,
    noEarlyStart: false,
    pay: false,
    payAmount: null,
    payUsd: true,
    showPayToExecutor: false,
    recurrentEnabled: false,
    recurrentData: null,
    recurrentAllowOverlap: false,
    answers: [],
    termEnabled: false,
    termDays: null,
});

const toPayload = (
    form: DirFormState,
    disableRecurrence = false,
    hideFromDirectory = false,
): TaskTemplateSaveDataType => ({
    taskTypeId: form.taskTypeId,
    categoryId: form.categoryId,
    taskName: String(form.taskName || '').trim(),
    taskInfo: form.taskInfo || '',
    executorId: form.executor?.ID ?? null,
    controllerId: form.controller?.ID ?? null,
    reserveExecutorTime: Boolean(form.reserveExecutorTime),
    executorCanChangeTerm: Boolean(form.executorCanChangeTerm),
    noEarlyStart: Boolean(form.noEarlyStart),
    pay: Boolean(form.pay),
    payAmount: form.pay ? Number(form.payAmount || 0) : null,
    payUsd: form.payUsd !== false,
    showPayToExecutor: Boolean(form.pay && form.showPayToExecutor),
    recurrentEnabled: disableRecurrence ? false : Boolean(form.recurrentEnabled),
    recurrentData: disableRecurrence || !form.recurrentEnabled ? null : form.recurrentData,
    recurrentAllowOverlap: disableRecurrence
        ? false
        : Boolean(form.recurrentEnabled && form.reserveExecutorTime && form.recurrentAllowOverlap),
    answers: form.answers || [],
    termDays: form.termEnabled ? form.termDays : null,
    ...(hideFromDirectory ? { hidden: true } : {}),
});

const cardToForm = (card: TaskTemplateCardType): DirFormState => ({
    taskTypeId: card.taskType,
    categoryId: card.categoryId,
    taskName: card.taskName,
    taskInfo: card.taskDescription || '',
    executor: templateUserToForm(card.executorUser),
    controller: templateUserToForm(card.controllerUser),
    reserveExecutorTime: Boolean(card.reserveExecutorTime),
    executorCanChangeTerm: Boolean(card.executorCanChangeTerm),
    noEarlyStart: Boolean(card.noEarlyStart),
    pay: Boolean(card.pay),
    payAmount: card.payAmount ?? null,
    payUsd: card.payUsd !== false,
    showPayToExecutor: Boolean(card.showPayToExecutor),
    recurrentEnabled: Boolean(card.recurrentEvent),
    recurrentData: card.recurrentData || null,
    recurrentAllowOverlap: Boolean(card.recurrentAllowOverlap),
    answers: card.answers || [],
    termEnabled: (card.termDays ?? 0) >= 1,
    termDays: card.termDays ?? null,
});

export const useOffTaskDir = ({
    show,
    templateId,
    onHide,
    onSaved,
    disableRecurrence = false,
    forkFromTemplateId = null,
    hideFromDirectory = false,
}: UseOffTaskDirParams) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const { needPay } = useNeedPay();
    const [form, setForm] = useState<DirFormState>(emptyForm);
    const [files, setFiles] = useState<LeadCloudFileType[]>([]);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [showDescription, setShowDescription] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);

    useEffect(() => {
        if (!show) return;

        let cancelled = false;
        setActiveTab('general');
        setPendingFiles([]);
        setCloudProgress(false);

        if (!templateId) {
            setForm(emptyForm());
            setFiles([]);
            setShowDescription(false);
            setLoading(false);
            return;
        }

        const load = async () => {
            setLoading(true);
            const card = await getTaskTemplate(templateId);
            if (cancelled) return;
            if (!card) {
                showToast(t('common:messError'), 'danger');
                setLoading(false);
                onHide();
                return;
            }
            const nextForm = cardToForm(card);
            setForm(
                disableRecurrence
                    ? { ...nextForm, recurrentEnabled: false, recurrentData: null, recurrentAllowOverlap: false }
                    : nextForm,
            );
            setFiles(card.files || []);
            setShowDescription(Boolean(String(card.taskDescription || '').replace(/<[^>]*>/g, '').trim()));
            setLoading(false);
        };

        void load();
        return () => {
            cancelled = true;
        };
        // Завантаження лише при відкритті картки
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, templateId, disableRecurrence]);

    const uploadPending = async (id: number): Promise<boolean> => {
        if (!pendingFiles.length) return true;
        const filesMeta = pendingFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));
        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, { taskTemplateId: id });
        if (!uploadConfigs || uploadConfigs.length !== pendingFiles.length) return false;

        setCloudProgress(1);
        const uploaded = await uploadFilesWithOverallProgress(pendingFiles, uploadConfigs, setCloudProgress);
        const cloudFiles = uploaded.filter((item) => item.success).map((item) => item.config);
        if (!cloudFiles.length) return false;
        const linked = await linkCloudflareFiles(cloudFiles, { taskTemplateId: id });
        return Boolean(linked) && cloudFiles.length === pendingFiles.length;
    };

    const handleSave = async () => {
        if ((forkFromTemplateId || !templateId) && needPay()) return;
        const payload = toPayload(form, disableRecurrence, hideFromDirectory);
        if (!payload.taskName) {
            showToast(t('errEmptyName'), 'danger');
            setActiveTab('general');
            return;
        }
        if (payload.recurrentEnabled && !isRecurrentDataValid(payload.recurrentData)) {
            showToast(t('recurrent.errInvalid'), 'danger');
            setActiveTab('recurrent');
            return;
        }

        setSaving(true);
        try {
            let savedTemplate: TaskTemplateCardType | undefined;
            if (forkFromTemplateId) {
                const cloned = await cloneTaskTemplate(forkFromTemplateId, payload);
                if (!cloned?.stat || !cloned.template) {
                    showToast(
                        cloned?.errCode === 'TEMPLATE_FILES_COPY_FAILED'
                            ? t('processDir.errCopyFiles')
                            : t('common:messError'),
                        'danger',
                    );
                    return;
                }
                savedTemplate = cloned.template;
            } else if (!templateId) {
                const created = await createTaskTemplate(payload);
                if (!created?.stat || !created.templateId) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
                if (pendingFiles.length) {
                    const uploaded = await uploadPending(created.templateId);
                    if (!uploaded) showToast(t('common:messError'), 'warning');
                }
                savedTemplate = created.template;
            } else {
                const updated = await updateTaskTemplate(templateId, payload);
                if (!updated?.stat) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
                savedTemplate = updated.template;
            }
            showToast(t('directory.messSaved'), 'success');
            onSaved(savedTemplate);
            onHide();
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setSaving(false);
            setCloudProgress(false);
        }
    };

    return {
        form,
        setForm,
        files,
        setFiles,
        pendingFiles,
        setPendingFiles,
        showDescription,
        setShowDescription,
        activeTab,
        setActiveTab,
        loading,
        saving,
        cloudProgress,
        handleSave,
    };
};
